import { Component, input, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Step } from '../../../models/engineering/step';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { ValueAccessorDirective } from '../../../directives/value-accessor.directive';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { ItemDetailsFormGroup, ItemConsumableDetails } from '../../../models/engineering/item-consumable-details';
import { ItemMaster } from '../../../models/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { InputParameter } from '../../../models/engineering/input-parameter';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'app-step-details-viewer',
  templateUrl: './step-details-viewer.component.html',
  styleUrl: './step-details-viewer.component.scss',
  hostDirectives: [ValueAccessorDirective],
})
export class StepDetailsViewerComponent {
  private readonly valueAccessor = inject(ValueAccessorDirective<Step[]>);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly stepProxyService = inject(StepProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);

  readonly stepToAdd = new FormControl<Step | null>(null);
  readonly itemMastersCache = signal<Map<number, ItemMaster[]>>(new Map());
  readonly parametersControlsCache = new Map<number, FormControl<InputParameter[]>>();
  readonly itemDetailsFormGroupsCache = new Map<number, FormArray<FormGroup<ItemDetailsFormGroup>>>();

  canEdit = input<boolean>(false);
  canEditItems = input<boolean>(false);
  canEditParameters = input<boolean>(false);

  inventoryTypeReference = input<string | undefined>();

  steps = new FormArray<FormControl<Step>>([]);

  formGroup = new FormGroup({
    steps: this.steps,
  });

  constructor() {
    this.onValueChanges();
    this.setupStepToAddListener();
  }

  private setupStepToAddListener(): void {
    this.stepToAdd.valueChanges.subscribe((step) => {
      if (step) {
        this.addStep(step);
        this.stepToAdd.setValue(null);
      }
    });
  }

  private onValueChanges(): void {
    this.valueAccessor.value$.subscribe((stepsData: Step[]) => {
      if (stepsData && Array.isArray(stepsData) && stepsData.length > 0) {
        this.loadStepsIntoForm(stepsData);
      } else {
        this.steps.clear();
        this.clearCaches();
      }
    });

    this.steps.valueChanges.subscribe(() => {
      this.updateModel();
    });
  }

  private loadStepsIntoForm(stepsData: Step[]): void {
    this.steps.clear();
    this.clearCaches();

    stepsData.forEach((step) => {
      const stepControl = new FormControl<Step>(step, { nonNullable: true });
      this.steps.push(stepControl);
      this.initializeStepFormControls([step]);
    });
  }

  private clearCaches(): void {
    this.parametersControlsCache.clear();
    this.itemDetailsFormGroupsCache.clear();
    this.itemMastersCache.set(new Map());
  }

  public fetchSteps = (query: string): Observable<Step[]> =>
    this.stepProxyService.searchSteps({ stepName: query }).pipe(
      map((response) => {
        const addedStepIds = this.steps.controls.map((c) => c.value.stepId);
        return (response.data || []).filter((step) => !addedStepIds.includes(step.stepId));
      }),
    );

  addStep(step: Step): void {
    if (!step) return;

    const alreadyAdded = this.steps.controls.some((c) => c.value.stepId === step.stepId);

    if (alreadyAdded) return;

    const stepControl = new FormControl<Step>(step, { nonNullable: true });
    this.steps.push(stepControl);

    this.initializeStepFormControls([step]);

    this.updateModel();
  }

  private updateModel(): void {
    const stepsArray: Step[] = this.steps.controls.map((control) => control.value);
    this.valueAccessor.notifyValueChange(stepsArray);
    this.valueAccessor.notifyTouched();
  }

  removeStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      const stepId = this.steps.at(index).value.stepId;

      this.steps.removeAt(index);

      this.parametersControlsCache.delete(stepId);
      this.itemDetailsFormGroupsCache.delete(stepId);
      this.itemMastersCache.update((cache) => {
        const newCache = new Map(cache);
        newCache.delete(stepId);
        return newCache;
      });

      this.updateModel();
    }
  }

  getStepName(step: Step): string {
    return this.localeHelper.getLocalizedValue(step.name);
  }

  private updateStepProperty<K extends keyof Step>(stepId: number, property: K, value: Step[K]): void {
    const stepIndex = this.steps.controls.findIndex((c) => c.value.stepId === stepId);

    if (stepIndex !== -1) {
      const currentStep = this.steps.at(stepIndex).value;
      const updatedStep = {
        ...currentStep,
        [property]: value,
      };
      this.steps.at(stepIndex).setValue(updatedStep, { emitEvent: true });
    }
  }

  private initializeStepFormControls(stepsData: Step[]): void {
    stepsData.forEach((step) => {
      if (!this.parametersControlsCache.has(step.stepId)) {
        const control = new FormControl<InputParameter[]>(step.additionalParameters || [], {
          nonNullable: true,
        });
        this.parametersControlsCache.set(step.stepId, control);

        control.valueChanges.subscribe((params) => {
          this.updateStepProperty(step.stepId, 'additionalParameters', params);
        });
      }

      if (!this.itemDetailsFormGroupsCache.has(step.stepId)) {
        const emptyFormArray = new FormArray<FormGroup<ItemDetailsFormGroup>>([]);
        this.itemDetailsFormGroupsCache.set(step.stepId, emptyFormArray);

        emptyFormArray.valueChanges.subscribe((itemDetails) => {
          const convertedItems: ItemConsumableDetails[] = itemDetails.map((item) => ({
            itemCode: item.item?.itemCode || '',
            isPreferred: item.isPreferred ?? false,
            ingredientUnit: item.ingredientUnit!,
            scalingMethod: item.scalingMethod!,
            quantity: item.quantity ?? 0,
          }));
          this.updateStepProperty(step.stepId, 'itemDetails', convertedItems);
        });

        if (step.itemDetails && step.itemDetails.length > 0) {
          const itemCodes = step.itemDetails.map((item) => item.itemCode);
          this.loadItemMastersForStepSync(step.stepId, itemCodes, step.itemDetails);
        }
      }
    });
  }

  getParametersControl(step: Step): FormControl<InputParameter[]> {
    return this.parametersControlsCache.get(step.stepId) || new FormControl<InputParameter[]>([], { nonNullable: true });
  }

  getItemDetailsFormGroupsForStep(step: Step): FormArray<FormGroup<ItemDetailsFormGroup>> {
    return this.itemDetailsFormGroupsCache.get(step.stepId) || new FormArray<FormGroup<ItemDetailsFormGroup>>([]);
  }

  private loadItemMastersForStepSync(stepId: number, itemCodes: string[], itemDetails: ItemConsumableDetails[]): void {
    this.itemMasterProxyService.getItemCodesBatch({ itemCodes }).subscribe({
      next: (response) => {
        if (response.data) {
          const itemsWithLocalized = response.data.map((item: ItemMaster) => ({
            ...item,
            itemDisplayNameLocalized: this.localeHelper.getLocalizedValue(item.itemDisplayName),
          }));

          this.itemMastersCache.update((cache) => {
            const newCache = new Map(cache);
            newCache.set(stepId, itemsWithLocalized);
            return newCache;
          });

          const formArray = this.itemDetailsFormGroupsCache.get(stepId);
          if (formArray) {
            formArray.clear();
            itemDetails.forEach((itemDetail) => {
              const item = itemsWithLocalized.find((i) => i.itemCode === itemDetail.itemCode);
              if (item) {
                formArray.push(this.createItemDetailFormGroup(itemDetail, item));
              }
            });
          }
        }
      },
    });
  }

  private createItemDetailFormGroup(itemDetail: ItemConsumableDetails, item: ItemMaster): FormGroup<ItemDetailsFormGroup> {
    return new FormGroup<ItemDetailsFormGroup>({
      item: new FormControl<ItemMaster>(item, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isPreferred: new FormControl<boolean>(itemDetail.isPreferred ?? false, {
        nonNullable: true,
      }),
      ingredientUnit: new FormControl(itemDetail.ingredientUnit ?? null),
      scalingMethod: new FormControl(itemDetail.scalingMethod ?? null),
      quantity: new FormControl(itemDetail.quantity ?? 0),
    });
  }
}
